/**
 * TRAD-3656 — 조회 실패를 «막는 오류»와 «배경 오류»로 가른다(순수 함수, 전 화면 공유).
 *
 * react-query 는 재조회가 실패해도 이전 성공 data 를 지우지 않는다(isError=true · data 유지). 그런데 게이트가 `isError`
 * 만 보면 보이던 데이터가 ErrorState 로 바뀐다 — 재조회 계기(온라인 복귀 refetchOnReconnect 기본 true · 주기 재조회
 * 도미넌스 60초/김프 30초 · mutation invalidate)마다 그 위험이 있다(dev 실측 2026-10-08: 시세 표 30행 → ErrorState).
 *
 * - blocking  : 실패 + 보여줄 데이터 없음 → ErrorState(재시도).
 * - background: 실패 + 보여줄 데이터 있음 → 데이터 그대로 + 토스트(useBackgroundErrorToast, 장애 1회당 1번).
 * - none      : 실패 아님.
 * `hasData` 는 «그 화면이 지금 데이터를 그리는 조건»과 같아야 한다(배열 길이 / 값 존재 / infinite pages 길이).
 */
export type ErrorDisplay = 'blocking' | 'background' | 'none';

export const resolveErrorDisplay = ({ isError, hasData }: { isError: boolean; hasData: boolean }): ErrorDisplay =>
  !isError ? 'none' : hasData ? 'background' : 'blocking';

/**
 * TRAD-3657 — 화면 상태의 «우선순위»: 막는 오류(blocking)가 로딩보다 먼저.
 *
 * 로딩/스켈레톤 분기가 «데이터 없음»(예: 시세 표의 'pending' = 티커 미응답)만 보고 isError 를 안 보면, 첫 조회가 실패한 화면이
 * 스켈레톤에 영구히 갇힌다(dev 실측 2026-10-08: 마켓 목록 200 + 티커만 실패 → 30초 내내 스켈레톤 15줄, 오류·재시도 없음).
 * `isPending` 은 «아직 응답 전»을 뜻하는 화면별 조건(react-query isLoading · 응답 모양 판정 등)이다 — 실패했으면 더는 «전»이 아니다.
 * 반환: 'error'(ErrorState) | 'loading'(스켈레톤) | 'content'(데이터 또는 빈 상태 — 배경 오류면 데이터 유지 + 토스트).
 */
export type LoadView = 'error' | 'loading' | 'content';

export const resolveLoadView = ({
  errorDisplay,
  isPending,
}: {
  errorDisplay: ErrorDisplay;
  isPending: boolean;
}): LoadView => (errorDisplay === 'blocking' ? 'error' : isPending ? 'loading' : 'content');
